import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subject } from 'rxjs';
import { environment } from '@env/environment';
import { HttpClienteService } from '../http-cliente.service';
import { BrazeService } from '../braze/braze.service';
import { LocalStorageService } from '../local-storage.service';
import { NotificationItem, NotificationsResponse } from '@core/interfaces/notifications';

export interface NotificationParams {
  sector?: string;
  code?: string;
}

export type NotificationContext = 'cliente' | 'vendedor' | 'vendedor-cliente';

@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  private readonly url = environment.apiURL;

  /** Emite cuando se refrescó la lista por push de Braze (una sola llamada API por push) */
  readonly onPushRefresh$ = new Subject<NotificationItem[]>();
  private currentContext: NotificationContext = 'cliente';
  constructor(
    private readonly _http: HttpClienteService,
    private readonly brazeService: BrazeService,
    private readonly localStorageService: LocalStorageService,
    private readonly router: Router
  ) {
    this.brazeService.onPushReceived
      .pipe(filter(payload => payload !== null))
      .subscribe(() => this.refreshFromPush());
    this.applyNotificationContextFromUrl(this.router.url);
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd)
      )
      .subscribe((event) => {
        this.applyNotificationContextFromUrl(event.urlAfterRedirects);
      });
  }

  /**
   * Vendedor en /main o /tienda (con flujo “como cliente”) → notificaciones del cliente seleccionado (badge y API con sector+code).
   * Fuera de esas rutas → notificaciones de vendedor sin code.
   */
  private applyNotificationContextFromUrl(fullUrl: string): void {
    const path = fullUrl.split('?')[0];
    const user = this.localStorageService.getItem('user');
    const isVendedor = user?.type === 'Vendedor';
    const isClientFacingSellerRoute =
      path.startsWith('/main') || path.startsWith('/tienda');

    if (isVendedor) {
      this.currentContext = isClientFacingSellerRoute ? 'vendedor-cliente' : 'vendedor';
    } else {
      this.currentContext = 'cliente';
    }
  }
  /** Obtiene clientSelect desde localStorage (clave alicorp-clientSelect) */
  private getClientSelect(): { code: string; sector: string } | null {
    try {
      const raw = this.localStorageService.getItem('clientSelect');

      // 🔥 1. Si no hay nada → null
      if (!raw) return null;

      // 🔥 2. Si viene como string → parsear
      const client = typeof raw === 'string' ? JSON.parse(raw) : raw;

      // 🔥 3. Validación FUERTE (no vacíos, no undefined)
      const code = client?.code?.toString().trim();
      const sector = client?.sector?.toString().trim();

      if (code && sector) {
        return { code, sector };
      }

      // 🔥 4. Si está inválido → limpiar (opcional pero PRO)
      this.localStorageService.removeItem('clientSelect');

      return null;

    } catch (error) {

      // 🔥 limpiar si está corrupto
      this.localStorageService.removeItem('clientSelect');
      return null;
    }
  }

  /**
   * Contexto de notificaciones:
   * - cliente: usuario Cliente → sector de sectorSelect
   * - vendedor: Vendedor fuera de /main y /tienda (ej. /seleccionar-cliente) → sin params
   * - vendedor-cliente: Vendedor en /main o /tienda → sector y code del cliente seleccionado (misma campana que “cliente”)
   */
  /** Parámetros para la API según el contexto actual */
  getNotificationParams(): NotificationParams {
    const context = this.currentContext;
    if (context === 'cliente') {
      const sector = this.localStorageService.getItem('sectorSelect')?.sector ?? '';
      return { sector };
    }
    if (context === 'vendedor-cliente') {
      const clientSelect = this.getClientSelect();
      return {
        sector: clientSelect?.sector ?? '',
        code: clientSelect?.code ?? '',
      };
    }
    return {};
  }

  /** Badge naranja (cliente) o rosa (vendedor) según contexto */
  getBadgeVariant(): 'cliente' | 'vendedor' {
    return this.currentContext === 'vendedor-cliente' ? 'cliente' : 'vendedor';
  }

  private refreshFromPush(): void {
    const params = this.getNotificationParams();
    this.getNotificationsByToken(params).subscribe({
      next: (res: NotificationsResponse | NotificationItem[]) => {
        const list = Array.isArray(res) ? res : (res?.notifications ?? []);
        const mapped = list.map((item) => ({
          ...item,
          read: item.read ?? item.isNotified,
        }));
        this.onPushRefresh$.next(mapped);
      },
    });
  }

  /**
   * Obtiene notificaciones según el contexto actual.
   * - Cliente: /notifications-by-token?sector=XX
   * - Vendedor: /notifications-by-token
   * - Vendedor con cliente: /notifications-by-token?sector=XX&code=YY
   */
  getNotificationsByToken(params?: NotificationParams) {
    const p = params ?? this.getNotificationParams();
    const searchParams = new URLSearchParams();
    if (p.sector != null && p.sector !== '') searchParams.set('sector', p.sector);
    if (p.code != null && p.code !== '') searchParams.set('code', p.code);
    const queryString = searchParams.toString();
    const query = `${this.url}/wcl-document/notifications-by-token${queryString ? `?${queryString}` : ''}`;
    return this._http.get(query);
  }

  markNotificationAsNotified(id: string) {
    const query = `${this.url}/wcl-document/mark-notification-as-notified-by-id`;
    const payload = { id };
    return this._http.post(query, payload);
  }
  markNotificationAsAutoDismissed(id: string) {
    const query = `${this.url}/wcl-document/mark-notification-as-notified-by-id`;

    const payload = {
      id,
      updateHidden: true
    }

    return this._http.post(query, payload);
  }

}

